import React, { useState, useEffect, useCallback } from 'react';
import { Play, Pause, Settings, CheckCircle, AlertTriangle, X, RefreshCw, FileText, Zap } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

// ─── Constants ────────────────────────────────────────────────────────────────
const CYCLE_OPTIONS = [
  { value: 'weekly', label: 'Hebdomadaire', desc: 'Chaque lundi à 09h00' },
  { value: 'biweekly', label: 'Bimensuel', desc: 'Le 1er et 15 de chaque mois' },
  { value: 'monthly', label: 'Mensuel', desc: 'Le 1er de chaque mois' },
  { value: 'on_release', label: 'À la libération', desc: 'Dès validation du séquestre' },
];

const ROUTING_OPTIONS = {
  bank_transfer: { label: 'Virement bancaire', icon: '🏦', fields: ['iban', 'bic', 'bank_name', 'account_holder'] },
  mobile_money: { label: 'Mobile Money', icon: '📱', fields: ['phone', 'operator', 'account_name'] },
  paystack: { label: 'Paystack', icon: '💳', fields: ['paystack_id', 'account_name'] },
};

const OPERATORS = ['MTN Mobile Money', 'Orange Money', 'Wave', 'Moov Money'];

const TAX_RATES = {
  CI: { label: "Côte d\'Ivoire", rate: 0.10, form: 'Formulaire ITS (Impôt sur Traitements et Salaires)' },
  GH: { label: 'Ghana', rate: 0.125, form: 'Formulaire PAYE Ghana Revenue Authority' },
  FR: { label: 'France', rate: 0.20, form: 'Formulaire 2042 — Revenus de source étrangère' },
  US: { label: 'États-Unis', rate: 0.30, form: 'Formulaire W-8BEN (non-résidents US)' },
};

const formatAmount = (v, currency = 'XOF') => {
  if (!v && v !== 0) return '—';
  const opts = currency === 'XOF'
    ? { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }
    : { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return new Intl.NumberFormat('fr-FR', opts)?.format(v);
};

const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// ─── Tax Withholding Form Modal ───────────────────────────────────────────────
const TaxWithholdingModal = ({ recipient, onClose, onSave }) => {
  const [country, setCountry] = useState('CI');
  const [taxId, setTaxId] = useState('');
  const [exempt, setExempt] = useState(false);
  const [exemptReason, setExemptReason] = useState('');

  const taxInfo = TAX_RATES?.[country];
  const grossAmount = recipient?.commission_amount || 0;
  const withholdingAmount = exempt ? 0 : grossAmount * taxInfo?.rate;
  const netAmount = grossAmount - withholdingAmount;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Retenue Fiscale</h3>
            <p className="text-sm text-gray-500">{recipient?.recipient_name || 'Destinataire'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs text-amber-600 font-semibold mb-1">Montant brut</p>
            <p className="text-2xl font-bold text-amber-700">{formatAmount(grossAmount)}</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Pays de résidence fiscale</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(TAX_RATES)?.map(([code, info]) => (
                <button
                  key={code}
                  onClick={() => setCountry(code)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${country === code ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <p className="text-sm font-semibold text-gray-800">{info?.label}</p>
                  <p className="text-xs text-gray-500">Taux: {(info?.rate * 100)?.toFixed(1)}%</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Numéro d'identification fiscale (NIF)</label>
            <input
              type="text"
              value={taxId}
              onChange={e => setTaxId(e?.target?.value)}
              placeholder="Ex: CI-2024-XXXXXX"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-xs text-blue-600 font-semibold mb-1">Formulaire applicable</p>
            <p className="text-sm text-blue-800">{taxInfo?.form}</p>
          </div>

          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <input
              type="checkbox"
              id="exempt"
              checked={exempt}
              onChange={e => setExempt(e?.target?.checked)}
              className="w-4 h-4 text-amber-500 rounded"
            />
            <label htmlFor="exempt" className="text-sm text-gray-700 font-medium">Exonération fiscale (fournir justificatif)</label>
          </div>

          {exempt && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Motif d'exonération</label>
              <input
                type="text"
                value={exemptReason}
                onChange={e => setExemptReason(e?.target?.value)}
                placeholder="Convention fiscale, statut diplomatique..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              />
            </div>
          )}

          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Montant brut</span>
              <span className="font-medium">{formatAmount(grossAmount)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Retenue ({(taxInfo?.rate * 100)?.toFixed(1)}%)</span>
              <span className="text-red-600 font-medium">- {formatAmount(withholdingAmount)}</span>
            </div>
            <div className="border-t border-gray-200 pt-2 flex justify-between">
              <span className="font-bold text-gray-800">Montant net</span>
              <span className="font-bold text-green-700">{formatAmount(netAmount)}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
              Annuler
            </button>
            <button
              onClick={() => onSave({ recipient, country, taxId, exempt, exemptReason, withholdingAmount, netAmount, taxRate: taxInfo?.rate })}
              className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold transition-colors"
            >
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Schedule Config Modal ────────────────────────────────────────────────────
const ScheduleConfigModal = ({ onClose, onSave }) => {
  const [cycle, setCycle] = useState('monthly');
  const [routing, setRouting] = useState('bank_transfer');
  const [routingDetails, setRoutingDetails] = useState({});
  const [minAmount, setMinAmount] = useState(50000);
  const [autoApprove, setAutoApprove] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(true);

  const routingFields = ROUTING_OPTIONS?.[routing]?.fields || [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-lg font-bold text-gray-900">Configurer le cycle de règlement</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Fréquence de règlement</label>
            <div className="space-y-2">
              {CYCLE_OPTIONS?.map(opt => (
                <button
                  key={opt?.value}
                  onClick={() => setCycle(opt?.value)}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${cycle === opt?.value ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-800">{opt?.label}</p>
                    <p className="text-xs text-gray-500">{opt?.desc}</p>
                  </div>
                  {cycle === opt?.value && <CheckCircle className="w-4 h-4 text-amber-500" />}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Canal de routage par défaut</label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(ROUTING_OPTIONS)?.map(([key, opt]) => (
                <button
                  key={key}
                  onClick={() => setRouting(key)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${routing === key ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <div className="text-xl mb-1">{opt?.icon}</div>
                  <p className="text-xs font-medium text-gray-700">{opt?.label}</p>
                </button>
              ))}
            </div>
          </div>

          {routing === 'mobile_money' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Opérateur Mobile Money</label>
              <select
                value={routingDetails?.operator || ''}
                onChange={e => setRoutingDetails(d => ({ ...d, operator: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              >
                <option value="">Sélectionner un opérateur</option>
                {OPERATORS?.map(op => <option key={op} value={op}>{op}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Montant minimum de paiement (XOF)</label>
            <input
              type="number"
              value={minAmount}
              onChange={e => setMinAmount(parseInt(e?.target?.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">Les paiements inférieurs à ce montant seront regroupés</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div>
                <p className="text-sm font-medium text-gray-800">Approbation automatique</p>
                <p className="text-xs text-gray-500">Traiter sans validation manuelle</p>
              </div>
              <button
                onClick={() => setAutoApprove(!autoApprove)}
                className={`w-10 h-6 rounded-full transition-colors ${autoApprove ? 'bg-amber-500' : 'bg-gray-300'}`}
              >
                <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform mx-1 ${autoApprove ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div>
                <p className="text-sm font-medium text-gray-800">Notifications email</p>
                <p className="text-xs text-gray-500">Alerter les destinataires</p>
              </div>
              <button
                onClick={() => setNotifyEmail(!notifyEmail)}
                className={`w-10 h-6 rounded-full transition-colors ${notifyEmail ? 'bg-amber-500' : 'bg-gray-300'}`}
              >
                <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform mx-1 ${notifyEmail ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
              Annuler
            </button>
            <button
              onClick={() => onSave({ cycle, routing, routingDetails, minAmount, autoApprove, notifyEmail })}
              className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold transition-colors"
            >
              Enregistrer la configuration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AutoScheduledPayoutsPanel = () => {
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [taxModal, setTaxModal] = useState(null);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [toast, setToast] = useState(null);
  const [scheduleConfig, setScheduleConfig] = useState({
    cycle: 'monthly',
    routing: 'bank_transfer',
    minAmount: 50000,
    autoApprove: false,
    notifyEmail: true,
    active: false,
  });
  const [nextRunDate, setNextRunDate] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const computeNextRun = (cycle) => {
    const now = new Date();
    if (cycle === 'weekly') {
      const next = new Date(now);
      next?.setDate(now?.getDate() + (7 - now?.getDay() + 1) % 7 || 7);
      next?.setHours(9, 0, 0, 0);
      return next;
    } else if (cycle === 'biweekly') {
      const next = new Date(now);
      next?.setDate(now?.getDate() <= 15 ? 15 : 1);
      if (now?.getDate() > 15) next?.setMonth(next?.getMonth() + 1);
      next?.setHours(9, 0, 0, 0);
      return next;
    } else if (cycle === 'monthly') {
      const next = new Date(now.getFullYear(), now.getMonth() + 1, 1, 9, 0, 0);
      return next;
    }
    return null;
  };

  const fetchPendingPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('commission_payouts')
        ?.select('id, recipient_id, recipient_role, recipient_name, recipient_email, commission_amount, commission_rate, currency, status, created_at, escrow_transaction_id, tax_withholding_amount, tax_country, net_after_tax')
        ?.eq('status', 'pending')
        ?.order('created_at', { ascending: false })
        ?.limit(200);
      if (!error) setPendingPayouts(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingPayouts();
  }, [fetchPendingPayouts]);

  const toggleSelect = (id) => {
    setSelected(prev => prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    setSelected(selected?.length === pendingPayouts?.length ? [] : pendingPayouts?.map(p => p?.id));
  };

  const handleBatchProcess = async () => {
    if (selected?.length === 0) return;
    setBatchProcessing(true);
    try {
      const now = new Date()?.toISOString();
      const { error } = await supabase?.from('commission_payouts')
        ?.update({ status: 'released', released_at: now, updated_at: now })
        ?.in('id', selected);

      if (error) throw error;

      // Log batch action
      const logs = selected?.map(id => {
        const p = pendingPayouts?.find(x => x?.id === id);
        return {
          escrow_transaction_id: p?.escrow_transaction_id,
          action: 'commission_release',
          notes: `Traitement en lot — Cycle: ${scheduleConfig?.cycle} — Canal: ${scheduleConfig?.routing}`,
          amount_affected: p?.commission_amount,
          currency: 'XOF',
          metadata: { batch: true, cycle: scheduleConfig?.cycle, routing: scheduleConfig?.routing },
        };
      });
      await supabase?.from('escrow_action_logs')?.insert(logs);

      showToast(`${selected?.length} paiement(s) traité(s) avec succès`);
      setSelected([]);
      fetchPendingPayouts();
    } catch (e) {
      showToast('Erreur lors du traitement en lot', 'error');
    } finally {
      setBatchProcessing(false);
    }
  };

  const handleSaveTax = async ({ recipient, country, taxId, exempt, exemptReason, withholdingAmount, netAmount, taxRate }) => {
    try {
      const { error } = await supabase?.from('commission_payouts')?.update({
        tax_country: country,
        tax_id_number: taxId,
        tax_exempt: exempt,
        tax_exempt_reason: exemptReason,
        tax_withholding_amount: withholdingAmount,
        tax_rate_applied: taxRate,
        net_after_tax: netAmount,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', recipient?.id);

      if (!error) {
        showToast('Retenue fiscale enregistrée');
        setTaxModal(null);
        fetchPendingPayouts();
      }
    } catch (e) {
      showToast('Erreur lors de l\'enregistrement fiscal', 'error');
    }
  };

  const handleSaveSchedule = (config) => {
    setScheduleConfig({ ...config, active: true });
    setNextRunDate(computeNextRun(config?.cycle));
    setShowScheduleModal(false);
    showToast('Cycle de règlement configuré');
  };

  const totalSelected = pendingPayouts?.filter(p => selected?.includes(p?.id))?.reduce((s, p) => s + (p?.net_after_tax || p?.commission_amount || 0), 0);
  const totalPending = pendingPayouts?.reduce((s, p) => s + (p?.commission_amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Paiements Automatisés</h2>
          <p className="text-sm text-gray-500 mt-0.5">Planification, routage et retenues fiscales</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowScheduleModal(true)} className="flex items-center gap-2 px-4 py-2 border border-amber-300 text-amber-700 hover:bg-amber-50 rounded-xl text-sm font-medium transition-colors">
            <Settings className="w-4 h-4" />
            Configurer
          </button>
          <button onClick={fetchPendingPayouts} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-medium transition-colors">
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Schedule Status Card */}
      <div className={`rounded-xl border-2 p-5 ${scheduleConfig?.active ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${scheduleConfig?.active ? 'bg-green-100' : 'bg-gray-100'}`}>
              {scheduleConfig?.active ? <Play className="w-5 h-5 text-green-600" /> : <Pause className="w-5 h-5 text-gray-400" />}
            </div>
            <div>
              <p className="font-semibold text-gray-900">
                {scheduleConfig?.active ? 'Cycle actif' : 'Aucun cycle configuré'}
              </p>
              {scheduleConfig?.active && (
                <p className="text-sm text-gray-600">
                  {CYCLE_OPTIONS?.find(c => c?.value === scheduleConfig?.cycle)?.label} — {ROUTING_OPTIONS?.[scheduleConfig?.routing]?.label}
                </p>
              )}
            </div>
          </div>
          {scheduleConfig?.active && nextRunDate && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Prochain traitement</p>
              <p className="text-sm font-bold text-green-700">
                {nextRunDate?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            </div>
          )}
        </div>
        {scheduleConfig?.active && (
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
              Min: {formatAmount(scheduleConfig?.minAmount)}
            </span>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
              {scheduleConfig?.autoApprove ? '✅ Auto-approbation' : '⏳ Approbation manuelle'}
            </span>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
              {scheduleConfig?.notifyEmail ? '📧 Notifications actives' : '🔕 Sans notification'}
            </span>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'En attente', value: pendingPayouts?.length, suffix: 'paiements', color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Total à payer', value: formatAmount(totalPending), color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Sélectionnés', value: selected?.length, suffix: 'paiements', color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Montant sélectionné', value: formatAmount(totalSelected), color: 'text-green-600', bg: 'bg-green-50' },
        ]?.map((s, i) => (
          <div key={i} className={`${s?.bg} rounded-xl p-4`}>
            <p className="text-xs text-gray-500 mb-1">{s?.label}</p>
            <p className={`text-lg font-bold ${s?.color}`}>{s?.value} {s?.suffix || ''}</p>
          </div>
        ))}
      </div>

      {/* Batch Actions */}
      {selected?.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-semibold text-blue-800">
              {selected?.length} paiement(s) sélectionné(s) — {formatAmount(totalSelected)}
            </span>
          </div>
          <button
            onClick={handleBatchProcess}
            disabled={batchProcessing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-xl text-sm font-semibold transition-colors"
          >
            {batchProcessing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Traiter en lot
          </button>
        </div>
      )}

      {/* Payouts Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">Paiements en attente ({pendingPayouts?.length})</span>
          <button onClick={selectAll} className="text-xs text-amber-600 hover:text-amber-700 font-medium">
            {selected?.length === pendingPayouts?.length ? 'Désélectionner tout' : 'Sélectionner tout'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 w-10"></th>
                {['Destinataire', 'Rôle', 'Montant brut', 'Retenue fiscale', 'Montant net', 'Date', 'Actions']?.map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Chargement...</td></tr>
              ) : pendingPayouts?.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Aucun paiement en attente</td></tr>
              ) : pendingPayouts?.map(p => (
                <tr key={p?.id} className={`hover:bg-gray-50 transition-colors ${selected?.includes(p?.id) ? 'bg-blue-50' : ''}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected?.includes(p?.id)}
                      onChange={() => toggleSelect(p?.id)}
                      className="w-4 h-4 text-amber-500 rounded"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{p?.recipient_name || '—'}</p>
                      <p className="text-xs text-gray-400">{p?.recipient_email || '—'}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${p?.recipient_role === 'agent' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                      {p?.recipient_role === 'agent' ? '🏠 Agent' : '⚖️ Notaire'}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-gray-800">{formatAmount(p?.commission_amount)}</td>
                  <td className="px-4 py-3">
                    {p?.tax_withholding_amount ? (
                      <div>
                        <p className="text-red-600 font-medium text-xs">- {formatAmount(p?.tax_withholding_amount)}</p>
                        <p className="text-xs text-gray-400">{p?.tax_country} — {((p?.tax_rate_applied || 0) * 100)?.toFixed(1)}%</p>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Non défini</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-semibold text-green-700">
                    {formatAmount(p?.net_after_tax || p?.commission_amount)}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(p?.created_at)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setTaxModal(p)}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 hover:border-amber-400 hover:bg-amber-50 text-gray-700 hover:text-amber-700 rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
                    >
                      <FileText className="w-3 h-3" />
                      Retenue fiscale
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showScheduleModal && (
        <ScheduleConfigModal onClose={() => setShowScheduleModal(false)} onSave={handleSaveSchedule} />
      )}
      {taxModal && (
        <TaxWithholdingModal recipient={taxModal} onClose={() => setTaxModal(null)} onSave={handleSaveTax} />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-medium ${toast?.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
          {toast?.type === 'error' ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast?.msg}
        </div>
      )}
    </div>
  );
};

export default AutoScheduledPayoutsPanel;
